"use client"

import axios from "axios"
import { Formik, Form } from "formik"
import { useRouter } from "next/navigation"
import React, { useState } from "react"
import * as Yup from "yup"

import Button from "../Button"

import PressFormTextArea from "./PressFormTextArea"
import PressTextInput from "./PressTextInput"

interface FormValues {
  firstName: string
  lastName: string
  businessEmailAddress: string
  organization: string
  message: string
}

interface ApiResponse {
  success?: boolean
  message?: string
  error?: string
}

const PressForm: React.FC = () => {
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const PressFormValidationSchema = Yup.object({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    businessEmailAddress: Yup.string()
      .email("Invalid email address")
      .required("Required"),
    organization: Yup.string().required("Required"),
    message: Yup.string().required("Required"),
  })

  const handleSubmit = async (values: FormValues, { setSubmitting }: any) => {
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Submit form to our API route
      const response = await axios.post<ApiResponse>("/api/press-form", {
        values,
      })

      if (response.data.success) {
        router.push("/lp/thank-you-contact-press/")
      } else {
        throw new Error(response.data.error || "Submission failed")
      }
    } catch (error) {
      console.error("Form submission error:", error)

      // eslint-disable-next-line import/no-named-as-default-member
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.error || error.message
        setSubmitError(`Submission failed: ${errorMessage}`)
      } else {
        setSubmitError("An unexpected error occurred. Please try again.")
      }
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        businessEmailAddress: "",
        organization: "",
        message: "",
      }}
      validationSchema={PressFormValidationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Form onSubmit={formik.handleSubmit}>
          {submitError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {submitError}
            </div>
          )}

          <PressTextInput
            name="firstName"
            id="firstName"
            type="text"
            placeholder="First Name"
            disabled={isSubmitting}
          />
          <PressTextInput
            name="lastName"
            id="lastName"
            type="text"
            placeholder="Last Name"
            disabled={isSubmitting}
          />
          <PressTextInput
            name="businessEmailAddress"
            id="businessEmailAddress"
            type="text"
            placeholder="Business Email Address"
            disabled={isSubmitting}
          />
          <PressTextInput
            name="organization"
            id="organization"
            type="text"
            placeholder="Organization"
            disabled={isSubmitting}
          />
          <PressFormTextArea
            name="message"
            placeholder="Message"
            disabled={isSubmitting}
          />

          <Button
            onClick={(e) => {
              e.preventDefault()
              formik.handleSubmit()
            }}
            size="lg"
            type="submit"
            className="mt-12"
            disabled={isSubmitting || !formik.isValid}
          >
            {isSubmitting ? "Submitting…" : "Contact Press"}
          </Button>
        </Form>
      )}
    </Formik>
  )
}

export default PressForm
